<script lang="ts" setup>
import { ref, computed, type PropType } from 'vue'
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue'
const { hasIntermediateAccess } = useAuth();

interface StatusItem {
  timestamp: string | Date
  holeNo: number
  amount: number
  pressure: number
  eventTypeId: number
  eventType: string
  startTime: string | Date
  stopTime: string | Date
  totalTime: string
  totalTimeMs: number
}

const props = defineProps({
  statusData: {
    type: Array as PropType<StatusItem[]>,
    required: true,
    default: () => []
  }
});

const formatDate = (date: any) => {
  if (!date) return "--";
  const d = new Date(date);
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatTime = (date: string | Date) => {
  if (!date) return '--:--:--'
  return new Date(date).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

const fmt = (val: number | undefined) => {
  if (val === undefined || val === null) return '0';
  return val.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const getStatusColor = (eventType: string) => {
  const s = (eventType || '').toLowerCase();
  if (s.includes('reached')) return 'success';
  if (s.includes('not finished')) return 'warning';
  if (s.includes('error') || s.includes('stop')) return 'error';
  return 'info';
};


const statusStats = computed(() => {
  const data = props.statusData || [];
  const total = data.length;
  if (total === 0) return { finished: 0, pending: 0, finishedPct: 0, pendingPct: 0 };

  const finished = data.filter(i => {
    const s = (i.eventType || '').toLowerCase();
    return s.includes('reached');
  }).length;

  const pending = data.filter(i => {
    const s = (i.eventType || '').toLowerCase();
    return s.includes('not finished');
  }).length;

  return {
    finished,
    pending,
    finishedPct: (finished / total) * 100,
    pendingPct: (pending / total) * 100
  };
});
const keyMetrics = computed(() => {
  const data = props.statusData || [];
  if (data.length === 0) return { totalDuration: '00:00:00', avgDuration: '00:00:00' };

  let totalSeconds = 0;
  data.forEach(item => {
    if (item.totalTimeMs) {
      totalSeconds += item.totalTimeMs / 1000;
    }
  });

  const avgSeconds = totalSeconds / data.length;
  
  const formatSec = (sec: number) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  return {
    totalDuration: formatSec(totalSeconds),
    avgDuration: formatSec(avgSeconds)
  };
});

const headers = [
  { title: 'Hole No', key: 'holeNo', align: 'start' },
  { title: 'Current Status', key: 'eventType', align: 'start' },
  { title: 'Max Amount (L)', key: 'amount', align: 'end' },
  { title: 'Start Time', key: 'startTime', align: 'start' },
  { title: 'Stop Time', key: 'stopTime', align: 'start' },
  { title: 'Total Duration', key: 'totalTime', align: 'end' },
] as const;
</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-container fluid v-else>
    <v-alert
      v-if="!statusData || statusData.length === 0"
      type="info"
      variant="tonal"
      text="No Status/Hole data available."
      class="mb-4"
    ></v-alert>

    <template v-else>
      <v-row class="mb-6">
        <!-- Hole Status Card -->
        <v-col cols="12" md="6">
          <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              Hole Status
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-6">
              <div class="d-flex flex-column gap-4">
                <!-- Custom Progress Bar -->
                <div class="d-flex rounded-pill overflow-hidden bg-grey-lighten-3" style="height: 12px; width: 100%">
                  <div class="bg-success transition-swing" :style="{ width: statusStats.finishedPct + '%' }" title="Finished"></div>
                  <div class="bg-warning transition-swing" :style="{ width: statusStats.pendingPct + '%' }" title="Not Finished"></div>
                </div>
                
                <!-- Legend -->
                <div class="d-flex justify-space-between text-caption text-medium-emphasis mt-2">
                  <div class="d-flex align-center">
                    <v-badge dot color="success" inline class="mr-2"></v-badge>
                    <span>Finished: <strong class="text-high-emphasis">{{ statusStats.finished }}</strong></span>
                  </div>
                  <div class="d-flex align-center">
                    <v-badge dot color="warning" inline class="mr-2"></v-badge>
                    <span>Pending: <strong class="text-high-emphasis">{{ statusStats.pending }}</strong></span>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Production Summary Card -->
        <v-col cols="12" md="6">
          <v-card class="h-100" elevation="2">
            <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
              Production Summary
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-6">
              <div class="d-flex justify-space-around align-center h-100">
                <div class="text-center">
                  <div class="text-h4 font-weight-bold text-blue-grey-darken-3 mb-1">{{ keyMetrics.totalDuration }}</div>
                  <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Total Duration</div>
                </div>
                <v-divider vertical class="my-2" style="height: 40px"></v-divider>
                <div class="text-center">
                  <div class="text-h4 font-weight-bold text-blue-grey-darken-3 mb-1">{{ keyMetrics.avgDuration }}</div>
                  <div class="text-caption text-uppercase text-medium-emphasis font-weight-bold">Avg Duration</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Hole Status Details Table -->
      <v-card elevation="2">
        <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
          <span class="text-subtitle-1 font-weight-bold">Hole Status Details</span>
        </v-card-title>
        <v-divider></v-divider>

        <v-data-table
          :headers="headers"
          :items="props.statusData"
          density="compact"
          class="text-body-2"
          hover
        >
          <template v-slot:item.holeNo="{ item }">
            <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
              {{ item.holeNo }}
            </v-chip>
          </template>

          <template v-slot:item.eventType="{ item }">
            <v-chip size="small" :color="getStatusColor(item.eventType)" variant="tonal" label class="font-weight-bold text-uppercase">
              {{ item.eventType }}
            </v-chip>
          </template>

          <template v-slot:item.amount="{ item }">
            <span class="font-weight-medium font-monospace">{{ fmt(item.amount) }}</span>
          </template>

          <template v-slot:item.startTime="{ item }">
            <div class="d-flex flex-column">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.startTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.startTime) }}</span>
            </div>
          </template>

          <template v-slot:item.stopTime="{ item }">
            <div class="d-flex flex-column">
              <span class="text-caption text-medium-emphasis">{{ formatDate(item.stopTime) }}</span>
              <span class="text-body-2 font-weight-medium">{{ formatTime(item.stopTime) }}</span>
            </div>
          </template>

          <template v-slot:item.totalTime="{ item }">
            <div class="d-flex align-center justify-end font-weight-bold text-high-emphasis">
              <v-icon size="small" class="mr-1 text-medium-emphasis">mdi-timer-outline</v-icon>
              {{ item.totalTime }}
            </div>
          </template>
        </v-data-table>
      </v-card>
    </template>
  </v-container>
</template>

<style scoped>
.gap-4 {
  gap: 1rem;
}
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>